import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCartItems, removeItemFromCart, clearCart, groupBySeller, calculateCartTotals, calculateSellerShipping } from '../utils/cartUtils';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart, X, Trash2 } from 'lucide-react';

function CartItemCard(props) {
  return (
    <div className="stat-card p-4 flex items-center gap-4">
      <div className="w-16 h-20 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
        {props.image ? (
          <img src={props.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">No Img</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{props.name}</p>
        <p className="text-sm text-zinc-500">{props.year} {props.set}</p>
        <p className="text-sm text-zinc-500">{props.grade}</p>
        <p className="font-mono text-emerald-400 mt-1">${props.price}</p>
      </div>
      <button onClick={props.onRemove} className="p-2 text-zinc-500 hover:text-red-400 transition-colors">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

function ShippingLine(props) {
  return (
    <div className="flex justify-between text-zinc-400 ml-2">
      <span>{props.seller} ({props.count})</span>
      <span className="text-white">${props.amount}</span>
    </div>
  );
}

export function CartPage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var isAuthenticated = auth.isAuthenticated;
  
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [cartTotals, setCartTotals] = useState(null);

  useEffect(function() {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    var cartItems = getCartItems();
    setItems(cartItems);
    setCartTotals(calculateCartTotals(cartItems));
    setLoading(false);
  }, [isAuthenticated, navigate]);

  function handleRemoveItem(itemId) {
    removeItemFromCart(itemId);
    var newItems = getCartItems();
    setItems(newItems);
    setCartTotals(calculateCartTotals(newItems));
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Item removed');
  }

  function handleClearAll() {
    clearCart();
    setItems([]);
    setCartTotals(null);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Cart cleared');
  }

  function handleCheckoutSeller(sellerId) {
    var sellerItems = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].seller_id === sellerId) {
        sellerItems.push(items[i].id);
      }
    }
    sessionStorage.setItem('checkout_items', JSON.stringify(sellerItems));
    navigate('/checkout');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0F]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  var sellerGroups = groupBySeller(items);

  // Build seller group elements
  var groupElements = [];
  for (var g = 0; g < sellerGroups.length; g++) {
    var group = sellerGroups[g];
    var itemCount = group.item_count || group.items.length;
    var shipping = calculateSellerShipping(itemCount);
    var savings = (5.99 * itemCount) - shipping;
    var subtotal = group.subtotal || 0;
    
    var itemCards = [];
    for (var i = 0; i < group.items.length; i++) {
      var item = group.items[i];
      itemCards.push(
        <CartItemCard 
          key={item.id}
          image={item.image_url}
          name={item.player_name}
          year={item.year}
          set={item.set_name}
          grade={item.grade_company + ' ' + item.grade_value}
          price={item.price.toFixed(2)}
          onRemove={function(id) { return function() { handleRemoveItem(id); }; }(item.id)}
        />
      );
    }
    
    var sellerId = group.seller_id;
    groupElements.push(
      <div key={sellerId} className="stat-card overflow-hidden">
        <div className="px-4 py-3 bg-zinc-800/50 border-b border-zinc-700">
          <p className="text-sm text-zinc-400">Seller: <span className="text-white font-medium">{group.seller_name}</span></p>
        </div>
        <div className="p-4 space-y-3">
          {itemCards}
        </div>
        <div className="px-4 py-3 bg-zinc-800/30 border-t border-zinc-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400">Subtotal ({itemCount} items)</span>
            <span className="font-mono text-white">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400">Shipping</span>
            <span className="font-mono text-white">${shipping.toFixed(2)}</span>
          </div>
          {itemCount > 1 && (
            <p className="text-xs text-emerald-400 mb-3">
              You save ${savings.toFixed(2)} on shipping by combining!
            </p>
          )}
          <Button 
            onClick={function(sid) { return function() { handleCheckoutSeller(sid); }; }(sellerId)}
            className="w-full btn-primary"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Checkout ({itemCount} items)
          </Button>
        </div>
      </div>
    );
  }

  // Build shipping lines for summary
  var shippingLines = [];
  if (cartTotals && cartTotals.sellerShipping) {
    for (var s = 0; s < cartTotals.sellerShipping.length; s++) {
      var ss = cartTotals.sellerShipping[s];
      shippingLines.push(
        <ShippingLine 
          key={ss.seller_id}
          seller={ss.seller_name}
          count={ss.item_count}
          amount={ss.shipping.toFixed(2)}
        />
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0D0F]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button 
          onClick={function() { navigate(-1); }}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-white">Shopping Cart</h1>
            <p className="text-zinc-400 mt-1">{items.length} item{items.length !== 1 ? 's' : ''}</p>
          </div>
          {items.length > 0 && (
            <button 
              onClick={handleClearAll}
              className="flex items-center gap-2 text-zinc-500 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingCart className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <h2 className="text-xl text-white mb-2">Your cart is empty</h2>
            <p className="text-zinc-500 mb-6">Add some cards to get started!</p>
            <Button 
              onClick={function() { navigate('/marketplace'); }}
              className="btn-primary"
            >
              Browse Marketplace
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {groupElements}
              
              {sellerGroups.length > 1 && (
                <p className="text-center text-amber-400 text-sm">
                  Note: {sellerGroups.length} sellers = {sellerGroups.length} separate shipments
                </p>
              )}
            </div>
            
            {cartTotals && (
              <div className="lg:col-span-1">
                <div className="stat-card p-5 sticky top-4">
                  <h3 className="font-semibold text-white text-lg mb-4">Order Summary</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between text-zinc-400">
                      <span>Subtotal ({cartTotals.itemCount} items)</span>
                      <span className="text-white">${cartTotals.subtotal.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between text-zinc-400">
                      <span>Processing Fee (3.5%)</span>
                      <span className="text-white">${cartTotals.processingFee.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between text-zinc-400">
                      <span>Tax (8%)</span>
                      <span className="text-white">${cartTotals.tax.toFixed(2)}</span>
                    </div>
                    
                    <div className="border-t border-zinc-700 pt-3">
                      <p className="text-zinc-500 text-xs mb-2">Shipping by Seller:</p>
                      {shippingLines}
                    </div>
                    
                    <div className="border-t border-zinc-700 pt-3 flex justify-between">
                      <span className="font-medium text-white">Total Shipping</span>
                      <span className="text-white">${cartTotals.totalShipping.toFixed(2)}</span>
                    </div>
                    
                    <div className="border-t border-emerald-500/30 pt-3 flex justify-between">
                      <span className="font-bold text-white text-lg">Grand Total</span>
                      <span className="font-mono font-bold text-emerald-400 text-lg">${cartTotals.grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {sellerGroups.length === 1 && (
                    <Button 
                      onClick={function() { handleCheckoutSeller(sellerGroups[0].seller_id); }}
                      className="w-full btn-primary mt-6 h-12"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Checkout All
                    </Button>
                  )}
                  
                  {sellerGroups.length > 1 && (
                    <p className="text-xs text-zinc-500 mt-4 text-center">
                      Checkout each seller separately using the buttons above
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CartPage;
